"use client"

import type React from "react"

import { useState,useEffect } from "react"
import { X, Send } from "lucide-react"
import { Button } from "../ui/button"
import { SelectValue } from "../ui/select"
import { apiClient as fetchApi } from "../lib/utils"

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
}

export function FeedbackModal({ isOpen, onClose}: FeedbackModalProps) {
  const [formData, setFormData] = useState({
    requestType: "",
    title: "",
    description: "",
    priority: "",
    dateNeeded: "",
    category: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userName, setUserName] = useState<string>("")
  const [userEmail, setUserEmail] = useState<string>("")
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastType, setToastType] = useState<"success" | "error">("success")

  // Load user info from session or localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("userName")
    const storedEmail = localStorage.getItem("userEmail") // optional if saved in login
    if (storedUser) setUserName(storedUser)
    if (storedEmail) setUserEmail(storedEmail)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
    // Prepare payload based on backend API
    const requestDateIso = new Date().toISOString()
    const endDateIso = formData.dateNeeded ? new Date(formData.dateNeeded).toISOString() : requestDateIso

    const payload = {
      logId: 0,
      requestDate: requestDateIso,
      endDate: endDateIso,
      name: userName || "Unknown User",
      senderEmail: userEmail || "",
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      requestType: formData.requestType,
    } as const

    ////console.log("Sending payload:", payload)
    
    const result = await fetchApi.request("/Admin/sendFeedback", {
      method: "POST",
      body: JSON.stringify(payload),
    })
    ////console.log("Feedback submitted:", result)

    // Reset form and close modal
    setFormData({
      requestType: "",
      title: "",
      description: "",
      priority: "",
      dateNeeded: "",
      category: "",
    })
    

    // Show success toast
    setToastMessage("Feedback submitted successfully!")
    setToastType("success")
    setShowToast(true)
    setTimeout(() => {
      setShowToast(false)
      onClose()
    }, 3000)
  }
  catch (error) {
    console.error("Error submitting feedback:", error);
    setToastMessage("Failed to submit feedback. Please try again later.")
    setToastType("error")
    setShowToast(true)
    setTimeout(() => {
      setShowToast(false)
    }, 3000)
  } finally {
    setIsSubmitting(false);
  }
};

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`max-w-3xl h-175 rounded-lg shadow-xl bg-white text-gray-900`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b border-gray-200`}
        >
          <h2 className="text-xl font-semibold">System Feedback & Support Request</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-opacity-10 hover:bg-gray-100`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Request Type & Category in one row */}
          <div className="flex gap-4">
            {/* Request Type */}
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">
                Type of Request <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.requestType}
                onChange={(e) => handleInputChange("requestType", e.target.value)}
                required
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900`}
              >
              <option value="" disabled hidden>
    Select Request Type
  </option>
                <option value="Defect">Bug Report</option>
                <option value="Enhancement">Feature Request</option>
                <option value="Query">Technical Support</option>
                <option value="Configuration Change">General Feedback</option>
                <option value="Other">Training Request</option>
              </select>
            </div>
          </div>


          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">
                Title of Request <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-500">
                {formData.title.length}/500
              </span>
            </div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="Enter a brief title for your request"
              required
              maxLength={500}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900 placeholder-gray-500`}
            />
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">
                Description <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-500">
                {formData.description.length}/2000
              </span>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Please provide detailed information about your request, including steps to reproduce (for bugs) or specific requirements (for features)"
              required
              rows={4}
              maxLength={2000}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical bg-white border-gray-300 text-gray-900 placeholder-gray-500`}
            />
          </div>

          {/* Priority & Date Needed in one row */}
        <div className="flex gap-4">
          {/* Priority */}
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">
              Priority <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.priority}
              onChange={(e) => handleInputChange("priority", e.target.value)}
              required
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900`}
            ><option value="" disabled hidden>
              Select Priority
              </option>
              <option value="low">Low(Future Release)</option>
              <option value="medium">Medium(3 to 5 days)</option>
              <option value="high">High(1 to 2 days)</option>
              <option value="urgent">Urgent (Will be addressed immediately)</option>
            </select>
          </div>

          {/* Date Needed */}
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">When do you need this?</label>
            <input
              type="date"
              value={formData.dateNeeded}
              onChange={(e) => handleInputChange("dateNeeded", e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300 text-gray-900`}
            />
          </div>
        </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 bg-transparent"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            toastType === "success"
              ? "bg-white border-2 border-green-500 text-green-800"
              : "bg-white border-2 border-red-500 text-red-800"
          }`}
          style={{ minWidth: "300px" }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{toastMessage}</span>
            <button
              onClick={() => setShowToast(false)}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
